import { AnalyteConfig, BasicCartesianChartConfig, SeriesConfig } from '../../data/interface/widget-config';
import {
  BasicCartesianChartAxisTemplate,
  BasicCartesianChartEchartConfigTemplate,
} from '../../data/widget-config-templates/basic-cartesian-chart';
import { masterAnalytes } from './master-analytes';
import { color } from '../../client/clients/wastemanagement/taiao-dashboard-layout/colors';

/**
 * Generates a simple hash from a string
 * @param str The string to hash
 * @returns A numeric hash value
 */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Gets a consistent color for a given name based on its hash
 * @param name The name to get a color for
 * @returns A hex color from the predefined palette
 */
function getColorForName(name: string): string {
  const hash = simpleHash(name);
  const colorIndex = hash % color.length;
  return color[colorIndex];
}

export interface GenerateSeriesOptions {
  extendedName?: boolean;
  includeUnitsInName?: boolean;
  type?: string; // e.g. 'line', 'bar', etc. If not provided, defaults to 'line'
  useDataSourceColours?: boolean; // whether to generate colors for the series based on their names
}

export interface CreateChartOptions {
  includeUnitsInName?: boolean;
  includeAnalyteInName?: boolean;
}

export function generateSeries(
  dataSourceIds: string[],
  analyteIds: string[],
  options: GenerateSeriesOptions = {
    extendedName: false,
    type: 'line',
    useDataSourceColours: false,
  }
): SeriesConfig[] {
  const analytes = analyteIds.map((id) => masterAnalytes[id]).filter((a) => a !== undefined);

  const formatName = (entry: AnalyteConfig, dsId: string) => {
    return dataSourceIds.length > 1 || options.extendedName ? `${entry.name} (${dsId})` : entry.name;
  };

  return dataSourceIds.reduce((acc, dsId) => {
    return acc.concat(
      analytes.map(
        (entry) =>
          ({
            ...entry,
            dataSourceIdentitfier: dsId,
            name: formatName(entry, dsId),
            type: options.type || 'line',
            color: options.useDataSourceColours ? getColorForName(dsId) : entry.color,
          } as SeriesConfig)
      ) as []
    );
  }, []);
}

//
// configure a chart with a series
// note: this needs to be consolidated with other versions (e.g. Raukawa)
//
export function configureChart(
  title: string,
  series: SeriesConfig[],
  options: CreateChartOptions = { includeUnitsInName: true }
): BasicCartesianChartConfig {
  // count the number of unique dataSoureIdentifiers
  const uniqueDataSourceIdentifiers = Array.from(new Set(series.map((s) => s.dataSourceIdentitfier)));
  console.log(`Configuring chart "${title}" with ${uniqueDataSourceIdentifiers.length} unique data sources.`);

  // group analytes by unit
  const analytesByUnit = series.reduce((acc, curr) => {
    const unit = curr.unit || 'no unit';
    const existingUnit = acc.find((item) => item.unit === unit);
    if (existingUnit) {
      existingUnit.analytes.push(curr);
    } else {
      acc.push({ unit, analytes: [curr] });
    }
    return acc;
  }, [] as Array<{ unit: string; analytes: (typeof series)[0][] }>);

  const findYAxisIndex = (analytesByUnit: any[], series: SeriesConfig) => {
    return analytesByUnit.findIndex((unitGroup) => unitGroup.unit === series.unit);
  };

  const setInverse = (analyteConfig: AnalyteConfig) => {
    return analyteConfig.inverse || false;
  };

  // Dynamically expand chart side padding when additional y-axes are present.
  const leftAxisCount = Math.ceil(analytesByUnit.length / 2);
  const rightAxisCount = Math.floor(analytesByUnit.length / 2);
  const axisSlotWidthPx = 35;
  const baseSidePaddingPx = 15;
  const gridLeftPaddingPx = baseSidePaddingPx + (leftAxisCount - 1) * axisSlotWidthPx;
  const gridRightPaddingPx = baseSidePaddingPx + (rightAxisCount - 1) * axisSlotWidthPx;

  const cfg = {
    echartsConfig: {
      ...BasicCartesianChartEchartConfigTemplate,
      color: series.map((a) => (a.color ? a.color : undefined)).filter((c) => c !== undefined) as string[],
      grid: {
        top: '30%',
        left: gridLeftPaddingPx,
        right: gridRightPaddingPx,
        bottom: '6%',
        containLabel: true,
      },
      legend: {
        padding: [10, 10, 10, 10], // top, right, bottom, left
        show: true,
        data: series.map((aa) => aa.name),
        selected: series.reduce((acc, a) => {
          acc[a.name] = a.default;
          return acc;
        }, {} as Record<string, boolean>),
      },
      yAxis: analytesByUnit.map((entry, index) => {
        return {
          ...BasicCartesianChartAxisTemplate,
          type: 'value',
          position: index % 2 === 0 ? 'left' : 'right',
          offset: axisSlotWidthPx * Math.trunc(index / 2),
          name: entry.unit,
          inverse: setInverse(entry.analytes[0]),
          axisLine: {
            show: true,
            onZero: false,
            lineStyle: {
              color: entry.analytes[0].color,
            },
          },
          axisLabel: {
            color: entry.analytes[0].color,
            formatter: (value: number) => {
              // Format numbers to limit significant digits and remove floating-point precision issues
              const formatted = parseFloat(value.toPrecision(6));
              return formatted.toString();
            },
          },
        };
      }),
      series: series.map((entry, index) => ({
        type: entry?.type || 'line',
        name: entry.name,
        smooth: true,
        symbolSize: 7,
        yAxisIndex: findYAxisIndex(analytesByUnit, entry),
        showSymbol: true,
        connectNulls: true,
      })),
    },
    dataConfig: {
      title: title,
      widgetId: 1,
      domain: 'wai',
      dataSources: series.map((entry) => ({
        environmentalDataSourceIdentifier: entry.dataSourceIdentitfier,
        analyteIdentifier: entry.analyte,
        valueField: 'avg',
        label: entry.name,
      })),
      displayLabelOptions: {
        includeLocation: uniqueDataSourceIdentifiers.length > 1,
        includeAnalyte: options.includeAnalyteInName ?? true,
        includeUnitsInName: options.includeUnitsInName,
      },
    },
  } as BasicCartesianChartConfig;
  return cfg;
}
